import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAuth } from "../../AuthContext"; // same path Worker.jsx uses
import { getBackendUrl } from "../../api"; // adjust if api.js sits elsewhere
import Swal from 'sweetalert2';
import WorkerTickets from './WorkerTickets';

const DEFAULT_AVATAR = "/Images/Manurevalogo.png";

/* ─────────────────────────────────────────────
   Responsive hook (mirrors Worker.jsx)
───────────────────────────────────────────── */
const useWindowSize = () => {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
};

/**
 * NetworkManager — role dashboard.
 *
 *   MY DASHBOARD  — the NM's own view, styled like the Worker dashboard:
 *      welcome banner · EL/SL/Permission balance cards · Recent Leave Requests.
 *      Plus a "My Attendance" sub-tab. Uses the worker self-service endpoints.
 *   HOUSEKEEPING  — act on behalf of housekeeping staff (leave + attendance
 *      corrections) via /api/network-manager/*.
 *
 * Everything submitted (own or on-behalf) is PENDING → admin approval.
 */

const BASE = getBackendUrl();

async function api(path, opts = {}) {
  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.body && !isForm ? { "Content-Type": "application/json" } : {}) },
    ...opts,
  });
  let body = null;
  try { body = await res.json(); } catch { /* none */ }
  if (!res.ok) throw new Error(body?.error || body?.message || `Request failed (${res.status})`);
  return body;
}

const C = {
  pageBg: "#f0ede8", card: "#fff", ink: "#101F40", soft: "#444",
  muted: "#6b7280", faint: "#9ca3af", line: "#e8e4df", accent: "#4f6ef7",
  accentBg: "#eef1ff", green: "#15803d", greenBg: "#dcfce7", red: "#b91c1c",
  redBg: "#fee2e2", amber: "#b45309", amberBg: "#fef3c7", banner: "#101F40",
};
const FONT = "'DM Sans', sans-serif";

const TYPES = {
  casual:     { key: "casual",     card: "Earned Leave",  tag: "EL", tint: "#101F40", tintBg: "#eef1ff", unit: "days" },
  medical:    { key: "medical",    card: "Medical Leave", tag: "SL", tint: "#4caf4f", tintBg: "#ecfdf5", unit: "days" },
  permission: { key: "permission", card: "Permission",    tag: "PM", tint: "#f59e0b", tintBg: "#fffbeb", unit: "apps" },
};
const LEAVE_TYPE_OPTS = [
  { v: "casual", l: "Earned Leave" },
  { v: "medical", l: "Medical Leave" },
  { v: "permission", l: "Permission (2h)" },
];
const MONTHS = ["January","February","March","April","May","June",
  "July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const fmtDate = (v) => {
  if (!v) return "—";
  if (typeof v === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v.trim())) return v.trim();
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};
const fmtTime = (v) => {
  if (!v) return "—";
  if (/^\d{1,2}:\d{2}$/.test(v)) return v; // already HH:MM
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};
// HH:MM from either a plain "HH:MM" string or a full datetime/ISO value
const extractHHMM = (v) => {
  if (!v) return "";
  if (/^\d{1,2}:\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d)) return "";
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};
const dayOfWeek = (workDate) => {
  if (!workDate) return "—";
  const d = new Date(`${workDate}T00:00:00`);
  return isNaN(d) ? "—" : DOW[d.getDay()];
};
const fmtDuration = (min) => {
  if (min === null || min === undefined || isNaN(Number(min))) return "--";
  const m = Math.abs(Number(min));
  const sign = Number(min) < 0 ? "-" : "";
  return `${sign}${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, "0")}m`;
};
// best-effort duration when the API doesn't provide presence_min directly
const computeDuration = (day) => {
  if (day.presence_min !== undefined && day.presence_min !== null) return fmtDuration(day.presence_min);
  const inHHMM = extractHHMM(day.first_in), outHHMM = extractHHMM(day.last_out);
  if (!inHHMM || !outHHMM) return "--";
  const [ih, im] = inHHMM.split(":").map(Number), [oh, om] = outHHMM.split(":").map(Number);
  const mins = (oh * 60 + om) - (ih * 60 + im);
  return fmtDuration(mins);
};
const todayLong = () => new Date().toLocaleDateString("en-US",
  { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const statusStyle = (s) => {
  const u = String(s || "").toUpperCase();
  if (u.includes("APPROV")) return { bg: C.greenBg, fg: C.green };
  if (u.includes("REJECT") || u.includes("CANCEL")) return { bg: C.redBg, fg: C.red };
  return { bg: C.amberBg, fg: C.amber };
};
// /api/leave-history/user-requests returns camelCase keys (leaveType, startDate,
// dateApplied, ...). Map them to the names this page reads.
const normalizeRequest = (r = {}) => {
  const mgrComment = r.manager_comment ?? r.managerComments;
  return {
    ...r,
    leave_type:     r.leave_type     ?? r.leaveType,
    start_date:     r.start_date     ?? r.startDate,
    end_date:       r.end_date       ?? r.endDate,
    created_at:     r.created_at     ?? r.dateApplied ?? r.createdAt,
    days_requested: r.days_requested ?? r.daysRequested,
    is_half_day:    r.is_half_day    ?? r.isHalfDay,
    half_day_period: r.half_day_period ?? r.duration_period,
    employee_name:  r.employee_name  ?? r.employeeName,
    duration_text:  r.durationDisplay ?? r.duration,
    date_range:     r.dateRange,
    // backend fills "No comments" when empty - treat that as no comment
    manager_comment: mgrComment && mgrComment !== 'No comments' ? mgrComment : undefined,
  };
};

// Backfilled (HR-imported) leave records — hidden from the Network Manager's
// "Recent Leave Requests". They're tagged "Backfill: ..." in the comment/reason.
const isBackfillRequest = (r = {}) =>
  [r.comments, r.manager_comment, r.managerComments, r.reason]
    .some(v => /^\s*backfill\b/i.test(String(v || "")));

const typePill = (t) => {
  const lt = String(t || "").toLowerCase();
  if (lt.includes("casual") || lt.includes("earned")) return { bg: "#dbeafe", fg: "#1d4ed8", label: "Earned Leave" };
  if (lt.includes("medical") || lt.includes("sick")) return { bg: "#ede9fe", fg: "#6d28d9", label: "Medical Leave" };
  if (lt.includes("permission") || lt.includes("emergency")) return { bg: "#fce7f3", fg: "#be185d", label: "Permission" };
  return { bg: "#eef1f4", fg: C.soft, label: t || "—" };
};

function Toast({ toast }) {
  if (!toast) return null;
  const ok = toast.kind === "ok";
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999,
      background: ok ? C.greenBg : C.amberBg, color: ok ? C.green : C.amber,
      border: `1px solid ${ok ? "#bfe6cd" : "#f3d3b3"}`, borderRadius: 10,
      padding: "12px 16px", fontSize: 14, fontWeight: 500, maxWidth: "min(360px, calc(100vw - 40px))",
      fontFamily: FONT, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}>{toast.msg}</div>
  );
}

const input = { width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1px solid ${C.line}`, fontSize: 14, color: C.ink, background: "#fff",
  outline: "none", boxSizing: "border-box", fontFamily: FONT };
const cell = { padding: "14px 16px", borderTop: `1px solid #f0ede8`, color: "#444", fontSize: 13, fontFamily: FONT };
const th = { textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 600,
  letterSpacing: "0.6px", textTransform: "uppercase", color: "#aaa",
  background: "#faf9f7", whiteSpace: "nowrap", fontFamily: FONT };

// ---------------------------------------------------------------------------
// Time helpers (permission) — same slots as Worker.jsx: 9:30 AM → 7:00 PM,
// 30-min steps, end time = start + 2 hours (filled in automatically).
// ---------------------------------------------------------------------------
const PERMISSION_HOURS = 2;
function fmt12h(hhmm) {
  if (!hhmm) return "";
  const d = new Date(`2000-01-01T${hhmm}`);
  return isNaN(d) ? hhmm : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}
const TIME_SLOTS = (() => {
  const out = [];
  for (let mins = 9 * 60 + 30; mins <= 19 * 60; mins += 30) {
    const v = `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
    out.push({ v, l: fmt12h(v) });
  }
  return out;
})();
function addHoursHHMM(hhmm, hours) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + hours * 60;
  const hh = String(Math.floor((total / 60) % 24)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}
function validateMedicalFile(file) {
  if (!file) return { ok: true };
  if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name || ""))
    return { ok: false, error: "Only PDF files are allowed." };
  if (file.size > 5 * 1024 * 1024) return { ok: false, error: "File must be under 5MB." };
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const isoOf = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const todayISO = () => { const n = new Date(); return isoOf(n.getFullYear(), n.getMonth(), n.getDate()); };
const fmtDay = (iso) => iso
  ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
  : "";
const daysInclusive = (fromIso, toIso) =>
  Math.round((new Date(`${toIso}T00:00:00`) - new Date(`${fromIso}T00:00:00`)) / 86400000) + 1;

// Holidays (fetched once per page load) — calendar greys these out like Worker.jsx
let _holidayCache = null;
function useHolidays() {
  const [list, setList] = useState(_holidayCache || []);
  useEffect(() => {
    if (_holidayCache) return;
    let cancelled = false;
    api("/api/holidays").then(d => {
      const arr = Array.isArray(d) ? d : (d?.holidays || []);
      _holidayCache = arr.map(h => String(h?.date || h).slice(0, 10));
      if (!cancelled) setList(_holidayCache);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return list;
}

// ---------------------------------------------------------------------------
// Overlap check — port of Worker.jsx's checkDateOverlap so the NM sees the same
// "you already have a request on these dates" message before submitting.
// ---------------------------------------------------------------------------
function parseFlexDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
const dayTs = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
const periodOf = (v) => {
  if (!v) return null;
  const s = String(v).toLowerCase();
  if (/\bpm\b/.test(s) || s.includes("afternoon")) return "pm";
  if (/\bam\b/.test(s) || s.includes("morning")) return "am";
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return parseInt(m[1], 10) < 12 ? "am" : "pm";
  return null;
};
function findOverlap(requests, { isPermission, start, end, isHalf, halfPeriod, fromTime }) {
  const none = { hasOverlap: false, conflictingRequest: null };
  try {
    const newStart = parseFlexDate(start), newEnd = parseFlexDate(end);
    if (!newStart || !newEnd) return none;
    const newPeriod = isPermission ? periodOf(fromTime) : (isHalf ? periodOf(halfPeriod) : null);
    let amTaken = false, pmTaken = false, permissionExists = false, conflict = null;
    const newStartTs = dayTs(newStart), newEndTs = dayTs(newEnd);
    const isNewSingleDay = newStartTs === newEndTs;

    for (const req of requests || []) {
      const st = String(req.status || "").replace(/_/g, " ").trim().toLowerCase();
      if (st === "rejected" || st === "cancelled" || st === "cancel") continue;
      const rs = parseFlexDate(req.startDate || req.start_date);
      if (!rs) continue;
      const reRaw = parseFlexDate(req.endDate || req.end_date);
      const reqStartTs = dayTs(rs);
      const reqEndTs = reRaw ? Math.max(dayTs(reRaw), reqStartTs) : reqStartTs;
      if (reqEndTs < newStartTs || reqStartTs > newEndTs) continue;
      if (!isNewSingleDay || reqStartTs !== reqEndTs) return { hasOverlap: true, conflictingRequest: req };

      const reqType = String(req.leaveType ?? req.leave_type ?? req.type ?? "").trim().toLowerCase();
      const dur = String(req.duration ?? "").toLowerCase();
      const rsn = String(req.reason ?? "").toLowerCase();
      const fb = String(req.duration_period || req.halfDayPeriod || req.half_day_period || req.period || "").toLowerCase();
      const isReqHalf = req.daysRequested === 0.5 || req.days_requested === 0.5 || req.isHalfDay === true ||
        req.is_half_day === true || dur.includes("half") || dur.includes("morning") || dur.includes("afternoon");
      let reqPeriod = null;
      if (/\bmorning\b/.test(rsn) || /\bam\b/.test(rsn) || dur.includes("morning") || fb.includes("morn") || fb === "am") reqPeriod = "am";
      if (/\bafternoon\b/.test(rsn) || /\bpm\b/.test(rsn) || dur.includes("afternoon") || fb.includes("after") || fb === "pm") reqPeriod = "pm";

      if (reqType === "permission" || reqType === "emergency") {
        permissionExists = true; conflict = req;
        if (reqPeriod === "am") amTaken = true;
        if (reqPeriod === "pm") pmTaken = true;
      } else if (isReqHalf) {
        conflict = req;
        if (reqPeriod === "pm") pmTaken = true; else amTaken = true;
      } else {
        conflict = req;
        if (dur.includes("morning")) amTaken = true;
        else if (dur.includes("afternoon")) pmTaken = true;
        else { amTaken = true; pmTaken = true; }
      }
    }

    if (isPermission && permissionExists) return { hasOverlap: true, conflictingRequest: conflict };
    if (amTaken && pmTaken) return { hasOverlap: true, conflictingRequest: conflict };
    if (isPermission) {
      if (!newPeriod) return { hasOverlap: true, conflictingRequest: conflict };
      if (newPeriod === "am" && amTaken) return { hasOverlap: true, conflictingRequest: conflict };
      if (newPeriod === "pm" && pmTaken) return { hasOverlap: true, conflictingRequest: conflict };
      return none;
    }
    if (isHalf) {
      if (newPeriod === "am" && amTaken) return { hasOverlap: true, conflictingRequest: conflict };
      if (newPeriod === "pm" && pmTaken) return { hasOverlap: true, conflictingRequest: conflict };
      return none;
    }
    if (amTaken || pmTaken || permissionExists) return { hasOverlap: true, conflictingRequest: conflict };
    return none;
  } catch { return none; }
}

// ---------------------------------------------------------------------------
// Calendar field — same behaviour as Worker.jsx's calendar: weekends and
// government holidays are disabled (red / struck-through), months navigable,
// closes on outside click. `min` blocks dates before the start date.
// ---------------------------------------------------------------------------
const CAL_DOW = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const calIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const fieldStyle = (active, accent) => ({
  width: "100%", minHeight: 44, padding: "10px 12px", borderRadius: 10,
  border: `1px solid ${active ? accent : "#e2ddd6"}`, background: "#fff",
  fontSize: 14, fontFamily: FONT, color: "#111827",
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  cursor: "pointer", boxSizing: "border-box", textAlign: "left",
});
const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, fontFamily: FONT };

function CalendarField({ value, onChange, placeholder, holidays, min, accent, align = "left" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const seed = () => value || min || todayISO();
  const [view, setView] = useState(() => ({ y: +seed().slice(0, 4), m: +seed().slice(5, 7) - 1 }));
  const holidaySet = useMemo(() => new Set(holidays || []), [holidays]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = () => {
    if (!open) { const s = seed(); setView({ y: +s.slice(0, 4), m: +s.slice(5, 7) - 1 }); }
    setOpen(o => !o);
  };
  const shift = (delta) => setView(v => {
    let m = v.m + delta, y = v.y;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const dim = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: dim }, (_, i) => i + 1)];
  const today = todayISO();

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button type="button" onClick={toggle} style={fieldStyle(open || !!value, accent)} aria-haspopup="dialog" aria-expanded={open}>
        <span style={{ color: value ? "#111827" : "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value ? fmtDay(value) : placeholder}
        </span>
        {calIcon}
      </button>
      {open && (
        <div style={{ position: "absolute", zIndex: 80, top: "100%", [align]: 0, marginTop: 4, width: 272, maxWidth: "calc(100vw - 48px)",
          background: "#fff", border: "1px solid #e8e4df", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", padding: 16, fontFamily: FONT }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <button type="button" onClick={() => shift(-1)} aria-label="Previous month"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1, padding: "0 4px" }}>&#8249;</button>
            <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{MONTHS[view.m]}, {view.y}</span>
            <button type="button" onClick={() => shift(1)} aria-label="Next month"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1, padding: "0 4px" }}>&#8250;</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 4 }}>
            {CAL_DOW.map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, padding: "3px 0",
                color: d === "Su" || d === "Sa" ? "#ef4444" : "#9ca3af" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const iso = isoOf(view.y, view.m, d);
              const dow = new Date(view.y, view.m, d).getDay();
              const isWE = dow === 0 || dow === 6;
              const isHol = holidaySet.has(iso);
              const beforeMin = !!min && iso < min;
              const disabled = isWE || isHol || beforeMin;
              const isSel = iso === value;
              const isToday = iso === today;
              return (
                <button key={iso} type="button" disabled={disabled}
                  onClick={() => { onChange(iso); setOpen(false); }}
                  title={isHol ? "Government holiday" : isWE ? "Weekend" : beforeMin ? "Before start date" : ""}
                  style={{ padding: "7px 0", border: "none", borderRadius: 6, fontSize: 13, textAlign: "center", fontFamily: FONT,
                    cursor: disabled ? "not-allowed" : "pointer",
                    background: isSel ? accent : isToday ? "#eff6ff" : "transparent",
                    color: isSel ? "#fff" : disabled ? ((isWE || isHol) ? "#f3a5a5" : "#d1d5db") : "#111827",
                    fontWeight: isSel || isToday ? 700 : 400,
                    textDecoration: isHol && !isSel ? "line-through" : "none" }}>{d}</button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#9ca3af" }}><span style={{ color: "#ef4444" }}>&#9679;</span> Weekend / Holiday</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leave form — same design + behaviour as the Worker "Apply for …" popup.
//   variant="modal"  : full popup (header w/ balance chip, scrollable body, footer)
//   variant="inline" : same fields rendered inside the page (Housekeeping tab)
// Auto-fill: picking From fills To (and moves To if it'd be before From); picking
// a permission start time fills the end time (+2h); calendars skip weekends/holidays.
// ---------------------------------------------------------------------------
function LeaveForm({ target, presetType, balances, existingRequests, managerInfo, onToast, onDone, onCancel, variant = "inline" }) {
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isModal = variant === "modal";
  const holidays = useHolidays();

  const [leaveType, setLeaveType] = useState(presetType || "casual"); // casual | medical | permission
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [permDate, setPermDate] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [reason, setReason] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState(null); // null | 'morning' | 'afternoon'
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [fetchedBal, setFetchedBal] = useState(null);
  const fileInputRef = useRef(null);

  const meta = TYPES[leaveType];
  const accent = meta.tint;
  const accentBg = meta.tintBg;
  const accentText = accent === "#101F40" ? "#4f6ef7" : accent;
  const isPermission = leaveType === "permission";
  const isMedical = leaveType === "medical";
  const bal = balances || fetchedBal;

  // Balance — own balances come from the dashboard; on-behalf fetches the employee's.
  useEffect(() => {
    if (balances) return;
    let cancelled = false;
    (async () => {
      try {
        let uid = target?.id;
        if (!uid) { const u = await api("/api/user"); uid = u?.id ?? u?.user?.id; }
        if (!uid) return;
        const r = await api(`/api/employee/${uid}/leave-balance`);
        if (!cancelled) setFetchedBal(r?.balances || null);
      } catch { if (!cancelled) setFetchedBal(null); }
    })();
    return () => { cancelled = true; };
  }, [target?.id, balances]);

  const balanceInfo = (() => {
    const b = bal?.[leaveType];
    if (!b) return null;
    const used = Number(b.used) || 0;
    const pending = Number(b.pending) || 0;
    if (leaveType === "casual") {
      const total = (b.allocated_days != null ? Number(b.allocated_days) : Number(b.allocated) || 0) + (Number(b.extra_leave_days) || 0);
      const avail = total - used - pending;
      return { avail, main: `${avail.toFixed(1)} days left`,
        detail: `${used.toFixed(1)} used${pending > 0 ? `, ${pending.toFixed(1)} pending` : ""}` };
    }
    const allocated = Number(b.allocated) || 0;
    const avail = b.remaining != null ? Number(b.remaining) : Math.max(0, allocated - used - pending);
    if (leaveType === "medical") {
      return { avail, allocated, main: `${avail.toFixed(1)} of ${allocated} days left`,
        detail: `${used.toFixed(1)} used${pending > 0 ? `, ${pending.toFixed(1)} pending` : ""}` };
    }
    return { avail, allocated, used, pending, main: `${avail} of ${allocated} left this month`,
      detail: `${used} used${pending > 0 ? `, ${pending} pending` : ""}` };
  })();

  const permLimitReached = isPermission && balanceInfo
    ? (balanceInfo.used + balanceInfo.pending) >= balanceInfo.allocated : false;

  // Derived values
  const toTime = addHoursHHMM(fromTime, PERMISSION_HOURS);
  const isSingleDay = !isPermission && !!fromDate && !!toDate && fromDate === toDate;
  const dayCount = (!isPermission && fromDate && toDate && toDate >= fromDate)
    ? (isSingleDay && isHalfDay ? 0.5 : daysInclusive(fromDate, toDate)) : 0;

  useEffect(() => { if (!isSingleDay && isHalfDay) { setIsHalfDay(false); setHalfDayPeriod(null); } }, [isSingleDay]); // eslint-disable-line
  useEffect(() => { setFormError(""); }, [leaveType, fromDate, toDate, permDate, fromTime, reason, isHalfDay, halfDayPeriod]);

  // Close on Esc (modal only)
  useEffect(() => {
    if (!isModal) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isModal, busy, onCancel]);

  // ── Auto-fill ──
  const onPickFrom = (v) => { setFromDate(v); if (!toDate || v > toDate) setToDate(v); };
  const onPickTo = (v) => { if (fromDate && v < fromDate) return; setToDate(v); };
  const changeType = (t) => {
    setLeaveType(t); setFromDate(""); setToDate(""); setPermDate(""); setFromTime("");
    setIsHalfDay(false); setHalfDayPeriod(null); setFile(null); setFileError("");
  };
  const pickDay = (key) => {
    if (key === "full") { setIsHalfDay(false); setHalfDayPeriod(null); }
    else { setIsHalfDay(true); setHalfDayPeriod(key); }
  };
  const dayChoice = !isHalfDay ? "full" : (halfDayPeriod === "afternoon" ? "afternoon" : "morning");

  const onPickFile = (e) => {
    const f0 = e.target.files?.[0]; e.target.value = null;
    if (!f0) return;
    const v = validateMedicalFile(f0);
    if (!v.ok) { setFileError(v.error); return; }
    setFileError(""); setFile(f0);
  };

  // Live overlap warning (own requests only — same wording as Worker)
  const liveConflict = (() => {
    if (target || !existingRequests?.length) return null;
    if (isPermission) {
      if (!permDate || !fromTime) return null;
      const c = findOverlap(existingRequests, { isPermission: true, start: permDate, end: permDate, fromTime });
      if (!c.hasOverlap) return null;
      const cr = c.conflictingRequest || {};
      return `Cannot apply for permission. You already have a ${cr.leaveType || cr.leave_type || "leave"} request (${cr.status || "pending"}) on this date.`;
    }
    if (!fromDate || !toDate) return null;
    const c = findOverlap(existingRequests, { isPermission: false, start: fromDate, end: toDate, isHalf: isHalfDay, halfPeriod: halfDayPeriod });
    if (!c.hasOverlap) return null;
    const cr = c.conflictingRequest || {};
    return `Cannot apply for leave. You already have a ${cr.leaveType || cr.leave_type || "leave"} request (${cr.status || "pending"}) that overlaps with these dates.`;
  })();

  const limitMsg = permLimitReached
    ? `The maximum of ${balanceInfo.allocated} permission application(s) for this month has been reached.` : "";

  const isValid = () => {
    if (reason.trim().length < 10) return false;
    if (file && fileError) return false;
    if (isPermission) return !!permDate && !!fromTime;
    return !!fromDate && !!toDate && toDate >= fromDate;
  };
  const canSubmit = !busy && isValid() && !liveConflict && !permLimitReached;

  // Summary line
  let summary = "";
  if (isPermission) {
    if (fromTime) summary = `${PERMISSION_HOURS} hours, ${fmt12h(fromTime)} to ${fmt12h(toTime)}`;
  } else if (dayCount > 0) {
    summary = isHalfDay
      ? `Half day, ${halfDayPeriod === "afternoon" ? "afternoon" : "morning"}`
      : `${dayCount} day${dayCount !== 1 ? "s" : ""}`;
  }
  const submitLabel = busy ? "Sending…"
    : (!isPermission && dayCount > 0 ? `Send request (${dayCount} day${dayCount !== 1 ? "s" : ""})` : "Send request");

  const submit = async () => {
    if (!canSubmit) return;
    setFormError("");

    // Medical balance guard (same as Worker)
    if (isMedical && balanceInfo && dayCount > balanceInfo.avail) {
      setFormError(`Insufficient Medical Leave balance. ${balanceInfo.avail} day(s) remaining.`);
      return;
    }

    // Earned-leave LOP confirmation (same as Worker)
    if (leaveType === "casual" && balanceInfo && dayCount > 0) {
      const lop = dayCount - Math.max(0, balanceInfo.avail);
      if (lop > 0) {
        const who = target ? "the employee's" : "your";
        const res = await Swal.fire({
          title: "Loss of Pay (LOP) Alert",
          html: `
            <div style="text-align:left; font-size:14px; line-height:1.8; color:#374151;">
              <p>This <strong>Earned Leave</strong> request will result in <strong style="color:#dc2626;">Loss of Pay (LOP)</strong>.</p>
              <div style="background:#fef2f2; border:1px solid #fca5a5; border-radius:8px; padding:14px; margin:12px 0;">
                <div style="display:flex; justify-content:space-between; margin-bottom:6px;"><span>Current Balance:</span><strong>${Math.max(0, balanceInfo.avail).toFixed(1)} days</strong></div>
                <div style="display:flex; justify-content:space-between; margin-bottom:6px;"><span>Requested Days:</span><strong>${dayCount.toFixed(1)} days</strong></div>
                <div style="display:flex; justify-content:space-between; border-top:1px solid #fca5a5; padding-top:8px; margin-top:4px;"><span style="color:#dc2626; font-weight:600;">LOP Days:</span><strong style="color:#dc2626;">${lop.toFixed(1)} days</strong></div>
              </div>
              <p style="color:#6b7280; font-size:13px;">⚠️ <strong>${lop.toFixed(1)} day(s)</strong> will be deducted from ${who} salary.</p>
              <p>Do you want to proceed?</p>
            </div>`,
          icon: "warning", showCancelButton: true,
          confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
          confirmButtonText: "Yes, Proceed", cancelButtonText: "Cancel", reverseButtons: true, width: "420px",
          customClass: { container: "swal-on-top" },
          didOpen: () => { const c = document.querySelector(".swal-on-top"); if (c) c.style.zIndex = "999999"; },
        });
        if (!res.isConfirmed) return;
      }
    }

    setBusy(true);
    try {
      let start_date = fromDate, end_date = toDate, finalReason = reason.trim(), days_requested = dayCount;
      if (isPermission) {
        start_date = permDate; end_date = permDate;
        finalReason += `\n\nTime: ${fmt12h(fromTime)} to ${fmt12h(toTime)}`;
        days_requested = PERMISSION_HOURS; // permission is stored in hours (same as Worker)
      }
      const half = !isPermission && isHalfDay;
      // Own requests use the same leave_type values as Worker.jsx (permission → "emergency")
      const apiType = (isPermission && !target) ? "emergency" : leaveType;

      if (!target && isMedical && file) {
        const fd = new FormData();
        fd.append("leave_type", "medical"); fd.append("start_date", start_date); fd.append("end_date", end_date);
        fd.append("reason", finalReason); fd.append("days_requested", String(days_requested));
        if (half) { fd.append("is_half_day", "true"); fd.append("half_day_period", halfDayPeriod); }
        fd.append("medical_document", file, file.name);
        await api("/api/apply-leave", { method: "POST", body: fd });
        onToast("ok", `${meta.card} request submitted — pending approval.`);
      } else {
        const payload = { leave_type: apiType, start_date, end_date, reason: finalReason, days_requested,
          is_half_day: half, ...(half ? { half_day_period: halfDayPeriod } : {}) };
        if (target) {
          await api("/api/network-manager/apply-leave", { method: "POST",
            body: JSON.stringify({ target_user_id: target.id, ...payload }) });
          onToast("ok", `${meta.card} request submitted for ${target.name} — pending approval.`);
        } else {
          await api("/api/apply-leave", { method: "POST", body: JSON.stringify(payload) });
          onToast("ok", `${meta.card} request submitted — pending approval.`);
        }
      }
      onDone && onDone();
    } catch (e) { setFormError(e.message || "Could not submit the request."); }
    finally { setBusy(false); }
  };

  const bannerText = liveConflict || limitMsg || formError;

  // ── Pieces ──
  const chipRow = balanceInfo && (
    <div style={{ marginTop: isModal ? 8 : 0, display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "4px 10px" }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: accentText, background: accentBg, padding: "3px 10px", borderRadius: 999 }}>
        {balanceInfo.main}
      </span>
      <span style={{ fontSize: 12.5, color: "#6b7280" }}>{balanceInfo.detail}</span>
    </div>
  );

  const approverLine = !target && managerInfo?.managerName && (
    <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#4b5563", minWidth: 0 }}>
      <svg aria-hidden="true" viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="#15803d" strokeWidth={2} style={{ flexShrink: 0 }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={managerInfo.managerEmail || ""}>
        Goes to <strong style={{ color: "#101F40", fontWeight: 600 }}>{managerInfo.managerName}</strong> for approval
        {!isMobile && managerInfo.managerEmail && <span style={{ color: "#9ca3af" }}> ({managerInfo.managerEmail})</span>}
      </span>
    </div>
  );

  const body = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, fontFamily: FONT,
      ...(isModal ? { flex: 1, overflowY: "auto", padding: isMobile ? "18px" : "22px 24px" } : {}) }}>

      {bannerText && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: 10, fontSize: 13, border: "1px solid",
          background: "#fef2f2", borderColor: "#fca5a5", color: "#991b1b" }}>{bannerText}</div>
      )}

      {/* Leave type selector — only when not launched from a specific balance card */}
      {!presetType && (
        <div>
          <span style={labelStyle}>Leave type</span>
          <select style={{ ...fieldStyle(false, accent), appearance: "auto" }} value={leaveType} onChange={e => changeType(e.target.value)}>
            {LEAVE_TYPE_OPTS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          {chipRow && <div style={{ marginTop: 10 }}>{chipRow}</div>}
        </div>
      )}

      {/* When */}
      {isPermission ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <span style={labelStyle}>Date</span>
            <CalendarField value={permDate} onChange={setPermDate} placeholder="Choose a date" holidays={holidays} accent={accent} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label htmlFor="lp-from-time" style={labelStyle}>Start time</label>
              <select id="lp-from-time" value={fromTime} onChange={e => setFromTime(e.target.value)}
                style={{ ...fieldStyle(!!fromTime, accent), appearance: "auto" }}>
                <option value="">Select</option>
                {TIME_SLOTS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <span style={labelStyle}>End time</span>
              <div style={{ ...fieldStyle(false, accent), cursor: "default", background: "#f9f8f6", color: fromTime ? "#374151" : "#9ca3af" }}>
                {fromTime ? fmt12h(toTime) : "Set automatically"}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 14 : 12 }}>
          <div>
            <span style={labelStyle}>From</span>
            <CalendarField value={fromDate} onChange={onPickFrom} placeholder="Start date" holidays={holidays} accent={accent} />
          </div>
          <div>
            <span style={labelStyle}>To</span>
            <CalendarField value={toDate} onChange={onPickTo} placeholder="End date" holidays={holidays} min={fromDate} accent={accent}
              align={isMobile ? "left" : "right"} />
          </div>
        </div>
      )}

      {/* Full / half day — single-day requests only */}
      {isSingleDay && (
        <div>
          <span style={labelStyle} id="lp-day-label">How long</span>
          <div role="radiogroup" aria-labelledby="lp-day-label"
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, padding: 4, background: "#f5f3ef", borderRadius: 12 }}>
            {[{ key: "full", label: "Full day", sub: "1 day" },
              { key: "morning", label: "Morning", sub: "Half day" },
              { key: "afternoon", label: "Afternoon", sub: "Half day" }].map(opt => {
              const on = dayChoice === opt.key;
              return (
                <button key={opt.key} type="button" role="radio" aria-checked={on} onClick={() => pickDay(opt.key)}
                  style={{ padding: isMobile ? "9px 4px" : "10px 8px", border: "none", borderRadius: 9, cursor: "pointer", fontFamily: FONT,
                    background: on ? "#fff" : "transparent", color: on ? "#101F40" : "#6b7280",
                    boxShadow: on ? "0 1px 3px rgba(16,31,64,0.15)" : "none", transition: "background 0.15s, color 0.15s" }}>
                  <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ fontSize: 11.5, marginTop: 2, color: on ? accentText : "#9ca3af" }}>{opt.sub}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div role="status" style={{ fontSize: 13.5, fontWeight: 500, padding: "10px 14px", borderRadius: 10,
          background: "#f7f6f3", color: "#374151", borderLeft: `3px solid ${accent}` }}>
          You're requesting <strong style={{ color: "#101F40" }}>{summary}</strong>
          {!isPermission && fromDate !== toDate ? ` (${fmtDay(fromDate)} to ${fmtDay(toDate)})` : ""}
        </div>
      )}

      {/* Reason */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <label htmlFor="lp-reason" style={labelStyle}>Reason</label>
          <span style={{ fontSize: 12, color: reason.length > 450 ? "#dc2626" : "#9ca3af" }}>{reason.length}/500</span>
        </div>
        <textarea id="lp-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
          placeholder="A short note for your manager, e.g. family function out of town"
          style={{ width: "100%", minHeight: isMobile ? 84 : 96, padding: "10px 12px", border: "1px solid #e2ddd6", borderRadius: 10,
            fontSize: 14, fontFamily: FONT, outline: "none", resize: "vertical", lineHeight: 1.5, boxSizing: "border-box", color: "#111827" }} />
        {reason.trim().length > 0 && reason.trim().length < 10 && (
          <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>
            Add {10 - reason.trim().length} more character{10 - reason.trim().length !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Medical certificate — own requests (the upload goes with /api/apply-leave) */}
      {isMedical && !target && (
        <div>
          <span style={labelStyle}>Medical certificate <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></span>
          {!file ? (
            <button type="button" onClick={() => fileInputRef.current?.click()}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1.5px dashed #d6d1c9",
                borderRadius: 10, background: "#faf9f7", cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
              <span aria-hidden="true" style={{ fontSize: 22 }}>📄</span>
              <span>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "#374151" }}>Attach a PDF</span>
                <span style={{ display: "block", fontSize: 12, color: "#9ca3af" }}>Up to 5 MB</span>
              </span>
            </button>
          ) : (
            <div style={{ padding: "10px 12px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span aria-hidden="true" style={{ fontSize: 20 }}>📄</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: "#065f46", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                  <div style={{ fontSize: 11.5, color: "#6b7280" }}>{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                </div>
              </div>
              <button type="button" onClick={() => { setFile(null); setFileError(""); }}
                style={{ background: "none", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 8, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Remove</button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={onPickFile} style={{ display: "none" }} />
          {fileError && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: "#b91c1c" }}>{fileError}</div>}
        </div>
      )}
    </div>
  );

  const footer = (
    <div style={{ display: "flex", gap: 10, background: "#fff", fontFamily: FONT,
      ...(isModal
        ? { padding: isMobile ? "12px 18px calc(12px + env(safe-area-inset-bottom, 0px))" : "16px 24px", borderTop: "1px solid #f0ede8" }
        : { marginTop: 20 }) }}>
      <button type="button" onClick={() => onCancel?.()} disabled={busy}
        style={{ padding: "12px 18px", background: "#fff", color: "#4b5563", border: "1px solid #e2ddd6", borderRadius: 10,
          fontSize: 14, fontWeight: 500, cursor: busy ? "not-allowed" : "pointer", fontFamily: FONT }}>Cancel</button>
      <button type="button" onClick={submit} disabled={!canSubmit}
        style={{ flex: 1, padding: 12, background: canSubmit ? "#101F40" : "#d1d5db", color: "#fff", border: "none", borderRadius: 10,
          fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: FONT, transition: "background 0.2s" }}>
        {submitLabel}
      </button>
    </div>
  );

  if (!isModal) return <div style={{ fontFamily: FONT }}>{body}{footer}</div>;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(16,31,64,0.55)", display: "flex",
      alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 9998,
      padding: isMobile ? 0 : 20, fontFamily: FONT }}>
      <style>{`
        .lp-modal button:focus-visible, .lp-modal select:focus-visible, .lp-modal textarea:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
        .lp-modal textarea:focus { border-color: ${accent} !important; }
        @keyframes lpSheet { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
        .lp-modal { animation: lpSheet 0.22s ease-out; }
        @media (prefers-reduced-motion: reduce) { .lp-modal { animation: none; } }
      `}</style>
      <div className="lp-modal" role="dialog" aria-modal="true" aria-labelledby="lp-title"
        style={{ background: "#fff", width: "100%", maxWidth: isMobile ? "100%" : 520, maxHeight: isMobile ? "94vh" : "90vh",
          borderRadius: isMobile ? "20px 20px 0 0" : 18, boxShadow: "0 24px 64px rgba(16,31,64,0.28)",
          display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header: type, balance, approver */}
        <div style={{ padding: isMobile ? "10px 18px 16px" : "22px 24px 18px", borderBottom: "1px solid #f0ede8", borderTop: `4px solid ${accent}` }}>
          {isMobile && <div aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 2, background: "#e2ddd6", margin: "0 auto 12px" }} />}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h2 id="lp-title" style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600, color: "#101F40", letterSpacing: "-0.3px" }}>
                Apply for {meta.card.toLowerCase()}
              </h2>
              {presetType && chipRow}
            </div>
            <button type="button" onClick={() => onCancel?.()} aria-label="Close"
              style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 10, border: "none", background: "#f5f3ef", color: "#6b7280",
                fontSize: 20, lineHeight: 1, cursor: "pointer" }}>×</button>
          </div>
          {approverLine}
        </div>
        {body}
        {footer}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card icons — mirror Worker.jsx's CardIconEL / CardIconSL / CardIconPM
// ---------------------------------------------------------------------------
function CardIcon({ typeKey, color, size = 22 }) {
  if (typeKey === "medical")
    return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>);
  if (typeKey === "permission")
    return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
  return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
}

// ---------------------------------------------------------------------------
// Balance card — mirrors Worker.jsx's leaveCard exactly (accent bar, icon,
// tag pill, big count, progress bar, used/avail + Apply footer)
// ---------------------------------------------------------------------------
function BalanceCard({ meta, bal, onApply }) {
  const allocated = Number(bal?.allocated) || 0;
  const used = Number(bal?.used) || 0;
  const pending = Number(bal?.pending) || 0;
  const avail = bal?.remaining != null ? Number(bal.remaining) : Math.max(0, allocated - used - pending);
  const pct = allocated > 0 ? Math.min(100, ((used + pending) / allocated) * 100) : 0;
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: "#fff", borderRadius: 16, padding: 20, border: `1px solid ${C.line}`,
        position: "relative", overflow: "hidden", fontFamily: FONT,
        transition: "transform 0.2s, box-shadow 0.2s",
        ...(hover ? { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,0.09)" } : {}) }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", borderRadius: "16px 0 0 16px", background: meta.tint }} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: meta.tintBg }}>
          <CardIcon typeKey={meta.key} color={meta.tint} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", padding: "4px 10px",
          borderRadius: 20, textTransform: "uppercase", background: meta.tintBg, color: meta.tint }}>{meta.tag}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#999", marginBottom: 4 }}>{meta.card}</div>
      <div style={{ fontSize: 30, fontWeight: 600, color: C.ink, letterSpacing: "-1px", lineHeight: 1 }}>
        {used}<span style={{ fontSize: 14, fontWeight: 400, color: "#bbb" }}> / {allocated} {meta.unit}</span>
      </div>
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0ede8" }}>
        <div style={{ height: 5, background: "#f0ede8", borderRadius: 3, marginBottom: 10, overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 3, width: `${pct}%`, background: meta.tint, transition: "width 0.8s ease" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "#aaa" }}>
            used: <strong style={{ color: "#555", fontWeight: 500 }}>{used}</strong>
            {pending > 0 && <span style={{ color: "#f59e0b" }}> +{pending} pend</span>}
            &nbsp;·&nbsp; avail: <strong style={{ color: "#555", fontWeight: 500 }}>{avail}</strong>
          </div>
          <button
            style={{ padding: "7px 18px", border: "none", borderRadius: 20, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: FONT, background: meta.tint, color: "#fff" }}
            onClick={() => onApply(meta.key)}>Apply</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// My overview (banner + cards + recent requests) — Worker-style
// ---------------------------------------------------------------------------
function MyOverview({ onToast, search }) {
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const [me, setMe] = useState(null);
  const [manager, setManager] = useState(null);
  const [balances, setBalances] = useState(null);
  const [allRequests, setAllRequests] = useState([]); // everything (used for overlap checks)
  const [loading, setLoading] = useState(true);
  const [applyType, setApplyType] = useState(null); // opens modal

  // Backfilled (HR-imported) rows are hidden from the table
  const requests = useMemo(() => allRequests.filter(r => !isBackfillRequest(r)), [allRequests]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const user = await api("/api/user");
      setMe(user);
      const uid = user?.id ?? user?.user?.id;
      const [mgr, bal, reqs] = await Promise.allSettled([
        api("/api/employee/manager-info"),
        uid ? api(`/api/employee/${uid}/leave-balance`) : Promise.resolve(null),
        api("/api/leave-history/user-requests"),
      ]);
      if (mgr.status === "fulfilled") setManager(mgr.value);
      if (bal.status === "fulfilled" && bal.value?.balances) setBalances(bal.value.balances);
      if (reqs.status === "fulfilled")
        setAllRequests((Array.isArray(reqs.value) ? reqs.value : (reqs.value?.requests || [])).map(normalizeRequest));
    } catch (e) { onToast("err", e.message); }
    finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const name = me?.name || me?.user?.name || "there";
  const mgrName = manager?.managerName || manager?.manager_name;
  const mgrEmail = manager?.managerEmail || manager?.manager_email;

  const filteredRequests = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return requests;
    return requests.filter(r => {
      const hay = [r.employee_name || name, r.leave_type || r.type, r.status,
        r.comments || r.manager_comment || r.reason].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [requests, search, name]);

  if (loading && !me) return <div style={{ color: C.muted, padding: 20 }}>Loading your dashboard…</div>;

  return (
    <div style={{ display: "grid", gap: isMobile ? 14 : 24, fontFamily: FONT }}>
      {/* welcome banner — mirrors Worker.jsx heroCard */}
      <div style={{ background: "#101F40", borderRadius: isMobile ? 14 : 20, padding: isMobile ? "16px" : "28px 32px", color: "#fff",
        display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center",
        flexDirection: isMobile ? "column" : "row",
        position: "relative", overflow: "hidden", flexWrap: "wrap", gap: 12 }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: isMobile ? 120 : 200, height: isMobile ? 120 : 200, background: "rgba(79,110,247,0.15)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: -60, right: 80, width: isMobile ? 80 : 150, height: isMobile ? 80 : 150, background: "rgba(139,92,246,0.10)", borderRadius: "50%" }} />
        <div style={{ zIndex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: isMobile ? 15 : 20, fontWeight: 600, color: "#fff", margin: 0, marginBottom: 6 }}>Welcome back, {name}!</h2>
          {mgrName ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap",
              background: "rgba(79,110,247,0.2)", border: "1px solid rgba(79,110,247,0.4)",
              borderRadius: 20, padding: "5px 12px" }}>
              <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="#8ba7ff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span style={{ fontSize: isMobile ? 11 : 12, color: "#8ba7ff" }}>Reporting Manager:</span>
              <strong style={{ fontSize: isMobile ? 11 : 12, color: "#c4d0ff", fontWeight: 500, wordBreak: "break-word" }}>
                {mgrName}
              </strong>
            </div>
          ) : null}
        </div>
        <div style={{ zIndex: 1, textAlign: isMobile ? "left" : "right" }}>
          <div style={{ fontSize: isMobile ? 11 : 12, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>Today</div>
          <div style={{ fontSize: isMobile ? 12 : 14, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>{todayLong()}</div>
        </div>
      </div>

      {/* balance cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(auto-fit, minmax(280px, 1fr))", gap: isMobile ? 10 : 16 }}>
        <BalanceCard meta={TYPES.casual}     bal={balances?.casual}     onApply={setApplyType} />
        <BalanceCard meta={TYPES.medical}    bal={balances?.medical}    onApply={setApplyType} />
        <BalanceCard meta={TYPES.permission} bal={balances?.permission} onApply={setApplyType} />
      </div>

      {/* recent requests */}
      <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${C.line}`, overflow: "hidden" }}>
        <div style={{ padding: isMobile ? "12px 14px" : "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f0ede8" }}>
          <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 600, color: C.ink }}>Recent Leave Requests</div>
        </div>
        {filteredRequests.length === 0 ? (
          <div style={{ padding: "24px 20px", color: C.muted, textAlign: "center" }}>
            {search ? "No requests match your search" : "No leave requests yet."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
              <thead><tr>
                {["Date Applied","Employee","Leave Type","Duration","Date Range","Status","Comments"].map(h =>
                  <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filteredRequests.map((r, i) => {
                  const st = statusStyle(r.status);
                  const tp = typePill(r.leave_type || r.type);
                  const s = r.start_date, e = r.end_date;
                  const dur = r.duration_text || (r.is_half_day ? "Half Day"
                    : (String(r.leave_type || "").toLowerCase().includes("permission")
                        ? `${r.days_requested ?? r.days ?? ""} hours`
                        : `${r.days_requested ?? r.days ?? ""} day(s)`));
                  return (
                    <tr key={r.id || i}>
                      <td style={cell}>{fmtDate(r.created_at || r.date_applied || r.applied_at)}</td>
                      <td style={{ ...cell, fontWeight: 600, color: C.ink }}>{r.employee_name || name}</td>
                      <td style={cell}>
                        <span style={{ background: tp.bg, color: tp.fg, padding: "4px 10px",
                          borderRadius: 8, fontSize: 12, fontWeight: 600 }}>{tp.label}</span>
                      </td>
                      <td style={cell}>{dur}</td>
                      <td style={cell}>{s === e ? fmtDate(s) : `${fmtDate(s)} → ${fmtDate(e)}`}</td>
                      <td style={cell}>
                        <span style={{ background: st.bg, color: st.fg, padding: "4px 12px",
                          borderRadius: 999, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>
                          {String(r.status || "pending").toLowerCase()}
                        </span>
                      </td>
                      <td style={{ ...cell, color: C.faint, fontStyle: "italic" }}>
                        {r.comments || r.manager_comment || r.reason || "No comments"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {applyType && (
        <LeaveForm variant="modal" target={null} presetType={applyType}
          balances={balances} existingRequests={allRequests}
          managerInfo={mgrName ? { managerName: mgrName, managerEmail: mgrEmail } : null}
          onToast={onToast}
          onDone={() => { setApplyType(null); loadAll(); }}
          onCancel={() => setApplyType(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attendance status badge — same palette as AttendanceEmployee.jsx / Worker.jsx
// ---------------------------------------------------------------------------
const ATT_STATUS = {
  on_time:      { label: "On Time",     color: "#15803d", bg: "#dcfce7" },
  soft_late:    { label: "Soft-late",   color: "#b45309", bg: "#fef3c7" },
  missed_core:  { label: "Missed-core", color: "#b91c1c", bg: "#fee2e2" },
  short_hours:  { label: "Short-hours", color: "#c2410c", bg: "#ffedd5" },
  needs_review: { label: "Review",      color: "#4b5563", bg: "#e5e7eb" },
  absent:       { label: "Absent",      color: "#c0392b", bg: "#fdeeee" },
  neutral:      { label: null,          color: "#8a8f98", bg: "#f1f2f4" },
};
const DAY_CLASS_LABEL = {
  Holiday: "Holiday", WeekOff: "Week-off", Leave: "Leave",
  CompOff: "Comp-off", HalfDay: "Half-day", Permission: "Permission",
};
function attendanceBadge(day) {
  const raw = String(day.status || "").toLowerCase().replace(/[\s_]+/g, "_").trim();
  const meta = ATT_STATUS[raw] || ATT_STATUS.neutral;
  const label = meta.label || DAY_CLASS_LABEL[day.day_class] || day.day_class || "Neutral";
  return { label, color: meta.color, bg: meta.bg, isNeutral: raw === "neutral" || raw === "" };
}

// Statuses that are fine as-is — no correction needed
const NO_CORRECTION_STATUS = new Set(["on_time", "ok", "neutral", ""]);

// ---------------------------------------------------------------------------
// Attendance panel — self (target=null) or on-behalf (target=employee) —
// pixel-matches Worker.jsx's AttendancePanel (hero w/ month label, summary
// strip, Date/Day/In/Out/Duration/Status/Action table, correction modal).
// A day with a pending correction shows a disabled "Correction pending" button
// (same behaviour as AttendanceEmployee.jsx) so only one can be filed per day.
// ---------------------------------------------------------------------------
function AttendancePanel({ target, onToast }) {
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // dates (YYYY-MM-DD) that already have a pending correction
  const [pendingDates, setPendingDates] = useState(() => new Set());
  const isPendingDay = (day) => pendingDates.has(String(day.work_date).slice(0, 10));

  // correction modal state
  const [corrDay, setCorrDay] = useState(null); // the row being corrected, or null
  const [corrFirstIn, setCorrFirstIn] = useState("");
  const [corrLastOut, setCorrLastOut] = useState("");
  const [corrReason, setCorrReason] = useState("");
  const [corrError, setCorrError] = useState("");
  const [corrSubmitting, setCorrSubmitting] = useState(false);

  const load = async () => {
    setLoading(true); setCorrDay(null);
    const monthUrl = target
        ? `/api/network-manager/attendance/${target.id}/month?year=${year}&month=${month}`
        : `/api/attendance/my-month?year=${year}&month=${month}`;
    const corrUrl = target
      ? `/api/network-manager/attendance/${target.id}/corrections` // new endpoint (see backend notes)
      : `/api/attendance/my-corrections`;                          // already exists
    try {
      const [m, c] = await Promise.allSettled([api(monthUrl), api(corrUrl)]);
      if (m.status === "fulfilled") setData(m.value);
      else { onToast("err", m.reason?.message || "Could not load attendance"); setData(null); }
      setPendingDates(new Set(
        c.status === "fulfilled" && Array.isArray(c.value)
          ? c.value.filter(x => x.state === "pending").map(x => String(x.work_date).slice(0, 10))
          : []
      ));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [target?.id]); // eslint-disable-line

  const summary = useMemo(() => {
    const days = data?.days || [];
    const c = { present: 0, on_time: 0, soft_late: 0, missed_core: 0, short_hours: 0, needs_review: 0 };
    days.forEach(d => {
      const raw = String(d.status || "").toLowerCase().replace(/[\s_]+/g, "_").trim();
      if (raw === "absent" || raw === "neutral" || raw === "") return;
      c.present++;
      if (c[raw] !== undefined) c[raw]++;
    });
    return c;
  }, [data]);
  const SUMMARY_CARDS = [
    ["Present days", summary.present],
    ["On-time days", summary.on_time],
    ["Soft-late days", summary.soft_late],
    ["Missed-core days", summary.missed_core],
    ["Short-hours days", summary.short_hours],
    ["Needs-review days", summary.needs_review],
  ];

  // No correction button for On Time / OK / neutral days
  const canRequestCorrection = (day) => {
    const raw = String(day.status || "").toLowerCase().replace(/[\s_]+/g, "_").trim();
    return !NO_CORRECTION_STATUS.has(raw);
  };

  const openCorrection = (day) => {
    if (isPendingDay(day)) return; // already submitted — one pending correction per day
    setCorrDay(day);
    setCorrFirstIn("");
    setCorrLastOut("");
    setCorrReason("");
    setCorrError("");
  };

  const submitCorrection = async () => {
    setCorrError("");
    const reason = corrReason.trim();
    if (reason.length < 5) { setCorrError("Please describe what happened (at least 5 characters)."); return; }
    const finalFirstIn = corrFirstIn || extractHHMM(corrDay.first_in);
    const finalLastOut = corrLastOut || extractHHMM(corrDay.last_out);
    if (!finalFirstIn) { setCorrError("There's no recorded In time for this day — please propose one."); return; }
    if (!finalLastOut) { setCorrError("There's no recorded Out time for this day — please propose one."); return; }

    setCorrSubmitting(true);
    try {
      const payload = {
        work_date: corrDay.work_date,
        corrected_first_in: `${corrDay.work_date}T${finalFirstIn}:00`,
        corrected_last_out: `${corrDay.work_date}T${finalLastOut}:00`,
        reason,
      };
      if (target) {
        await api("/api/network-manager/attendance/correction", { method: "POST",
          body: JSON.stringify({ target_user_id: target.id, ...payload }) });
      } else {
        await api("/api/attendance/my-correction", { method: "POST", body: JSON.stringify(payload) });
      }
      onToast("ok", "Correction submitted — pending admin approval.");
      const submittedDate = String(corrDay.work_date).slice(0, 10);
      setPendingDates(prev => new Set(prev).add(submittedDate));
      setCorrDay(null);
    } catch (e) {
      setCorrError(e.message || "Could not submit correction.");
      // server says one is already pending → lock the day in the UI too
      if (/already|pending/i.test(e.message || "")) {
        const dt = String(corrDay.work_date).slice(0, 10);
        setPendingDates(prev => new Set(prev).add(dt));
      }
    }
    finally { setCorrSubmitting(false); }
  };

  const days = data?.days || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, fontFamily: FONT }}>

      {/* hero / month label — mirrors Worker.jsx's attendance hero */}
      <div style={{ background: "#101F40", borderRadius: isMobile ? 14 : 20, padding: isMobile ? "16px" : "24px 32px", display: "flex",
        alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between",
        flexDirection: isMobile ? "column" : "row",
        position: "relative", overflow: "hidden", flexWrap: "wrap", gap: 12 }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: isMobile ? 120 : 160, height: isMobile ? 120 : 160, background: "rgba(79,110,247,0.15)", borderRadius: "50%" }} />
        <div style={{ zIndex: 1, minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? 15 : 20, fontWeight: 600, color: "#fff", marginBottom: 4 }}>
            {target ? `${target.name}'s Attendance` : "My Attendance"}
          </div>
          <div style={{ fontSize: isMobile ? 12 : 13, color: "rgba(255,255,255,0.5)", wordBreak: "break-word" }}>
            {target ? target.email : `${data?.employee?.name || ""}${data?.employee?.name ? " · " : ""}`}
            {!target && (data?.employee?.email || "")}
          </div>
        </div>
        <div style={{ zIndex: 1, display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: 15, color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>{MONTHS[month - 1]} {year}</span>
        </div>
      </div>

      {!loading && (!data || !data.employee) ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: 32, textAlign: "center", border: `1px solid ${C.line}` }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#101F40", marginBottom: 8 }}>Attendance Not Linked</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {target ? "This employee's account is not linked to a biometric device."
                    : "Your account is not linked to a biometric device. Please contact HR."}
          </div>
        </div>
      ) : (
        <>
          {/* summary strip */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : isTablet ? "repeat(3,1fr)" : "repeat(6,1fr)", gap: isMobile ? 8 : 12 }}>
            {SUMMARY_CARDS.map(([lbl, value]) => (
              <div key={lbl} style={{ background: "#fff", borderRadius: 14, border: `1px solid ${C.line}`, padding: isMobile ? "12px 14px" : "14px 16px" }}>
                <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: "#101F40", lineHeight: 1.1 }}>{loading ? "–" : value}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* daily table */}
          <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${C.line}`, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                <thead>
                  <tr style={{ background: "#fafbfc" }}>
                    {["Date","Day","In","Out","Duration","Status","Action"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#6b7280", borderBottom: `1px solid ${C.line}`, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk${i}`}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} style={{ padding: "12px 16px" }}>
                          <div style={{ height: 12, width: j === 6 ? 90 : 46, borderRadius: 6, background: "#eef0f3" }} />
                        </td>
                      ))}
                    </tr>
                  ))}

                  {!loading && days.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: "48px 16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                        No attendance records for {MONTHS[month - 1]} {year}.
                      </td>
                    </tr>
                  )}

                  {!loading && days.map(day => {
                    const badge = attendanceBadge(day);
                    return (
                      <tr key={day.work_date} style={{ borderBottom: "1px solid #f0f2f5", opacity: badge.isNeutral ? 0.6 : 1, fontStyle: badge.isNeutral ? "italic" : "normal" }}>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>{fmtDate(day.work_date)}</td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>{dayOfWeek(day.work_date)}</td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>{fmtTime(day.first_in)}</td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>{fmtTime(day.last_out)}</td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>{computeDuration(day)}</td>
                        <td style={{ padding: "11px 16px" }}>
                          <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: badge.bg, color: badge.color, whiteSpace: "nowrap" }}>{badge.label}</span>
                        </td>
                        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
                          {isPendingDay(day) ? (
                            <button disabled
                              style={{ border: "1px solid #e8e4df", color: "#9ca3af", background: "#f9fafb", padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "default", fontFamily: FONT }}>
                              Correction pending
                            </button>
                          ) : canRequestCorrection(day) ? (
                            <button onClick={() => openCorrection(day)}
                              style={{ border: "1px solid #101F40", color: "#101F40", background: "transparent", padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
                              Request correction
                            </button>
                          ) : (
                            <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* correction modal — mirrors Worker.jsx exactly */}
      {corrDay && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)", padding: 20, fontFamily: FONT }}>
          <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f0f2f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: "#101F40" }}>Request correction — {fmtDate(corrDay.work_date)} ({dayOfWeek(corrDay.work_date)})</span>
              <button onClick={() => setCorrDay(null)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>×</button>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Current In</label>
                  <input type="text" value={fmtTime(corrDay.first_in)} disabled
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e4df", borderRadius: 8, fontSize: 14, fontFamily: FONT, background: "#f9fafb", color: "#6b7280", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Current Out</label>
                  <input type="text" value={fmtTime(corrDay.last_out)} disabled
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e4df", borderRadius: 8, fontSize: 14, fontFamily: FONT, background: "#f9fafb", color: "#6b7280", boxSizing: "border-box" }} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Proposed In</label>
                  <input type="time" value={corrFirstIn} onChange={e => setCorrFirstIn(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e4df", borderRadius: 8, fontSize: 14, fontFamily: FONT, outline: "none", boxSizing: "border-box" }} />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Leave blank to keep the current In time</div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Proposed Out</label>
                  <input type="time" value={corrLastOut} onChange={e => setCorrLastOut(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e4df", borderRadius: 8, fontSize: 14, fontFamily: FONT, outline: "none", boxSizing: "border-box" }} />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Leave blank to keep the current Out time</div>
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Reason *</label>
                <textarea
                  value={corrReason}
                  onChange={e => setCorrReason(e.target.value)}
                  placeholder="Explain why the punch is missing or incorrect…"
                  maxLength={300}
                  style={{ width: "100%", minHeight: 80, padding: "10px 12px", border: "1px solid #e8e4df", borderRadius: 8, fontSize: 14, fontFamily: FONT, outline: "none", resize: "vertical", lineHeight: 1.5, boxSizing: "border-box" }}
                />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "right" }}>{corrReason.length}/300</div>
              </div>

              {corrError && (
                <div style={{ fontSize: 12.5, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>
                  {corrError}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={submitCorrection}
                  disabled={corrSubmitting}
                  style={{ flex: 1, padding: 12, background: corrSubmitting ? "#d1d5db" : "#101F40", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: corrSubmitting ? "not-allowed" : "pointer", fontFamily: FONT }}
                >
                  {corrSubmitting ? "Submitting…" : "Submit"}
                </button>
                <button
                  onClick={() => setCorrDay(null)}
                  style={{ padding: "12px 20px", background: "#fff", color: "#6b7280", border: "1px solid #e8e4df", borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: FONT }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// App shell — sidebar + topbar (mirrors the Worker layout)
// ---------------------------------------------------------------------------
function getInitials(name) {
  if (!name) return "NM";
  const p = String(name).trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "NM";
}

function SideIcon({ active, title, onClick, children, isMobile }) {
  return (
    <div onClick={onClick} title={title}
      style={{ width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 8, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        background: active ? "rgba(255,255,255,0.08)" : "transparent",
        boxShadow: active ? "inset 3px 0 0 #6ea8fe" : "none",
        transition: "background 0.2s ease, box-shadow 0.2s ease" }}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile picture modal — pixel-matches Worker.jsx's "Profile Picture" popup
// (gradient header card, current/preview photo, Upload from Device, Remove DP)
// ---------------------------------------------------------------------------
function ProfilePictureModal({ profilePicture, previewImage, isUploading, filePickerOpenRef,
  fileInputRef, onFileChange, onPickFile, onUpload, onRemove, onCancelPreview, onClose }) {
  return (
    <div onClick={() => { if (!filePickerOpenRef.current) onClose(); }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{
        maxWidth: 500, width: "90%", borderRadius: 16, overflow: "hidden",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)", fontFamily: FONT }}>

        {/* Header */}
        <div style={{ background: "rgba(255,255,255,0.95)", padding: "20px 24px",
          borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#1f2937",
            display: "flex", alignItems: "center", gap: 10 }}>
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="#667eea" strokeWidth={2}>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
            Profile Picture
          </h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 24,
            cursor: "pointer", color: "#6b7280", padding: 4, lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ background: "#fff", padding: "32px 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div style={{ textAlign: "center" }}>
              <img
                src={previewImage || profilePicture || DEFAULT_AVATAR}
                alt={previewImage ? "Preview" : "Current"}
                onError={e => { e.target.src = DEFAULT_AVATAR; }}
                style={{ width: 150, height: 150, borderRadius: "50%", objectFit: "cover",
                  border: "4px solid #667eea", boxShadow: "0 8px 24px rgba(102,126,234,0.3)" }} />
              <p style={{ marginTop: 12, fontSize: 14, color: "#6b7280" }}>
                {previewImage ? "Preview" : (profilePicture && profilePicture !== DEFAULT_AVATAR ? "Current Picture" : "Default Picture")}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {previewImage ? (
              <>
                <button onClick={onUpload} disabled={isUploading} style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", border: "none",
                  padding: "16px 24px", borderRadius: 12, fontSize: 16, fontWeight: 600,
                  cursor: isUploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 10, opacity: isUploading ? 0.7 : 1,
                  boxShadow: "0 4px 12px rgba(102,126,234,0.4)" }}>
                  {isUploading ? "Uploading…" : (<><svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#fff" strokeWidth={2}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Save Profile Picture</>)}
                </button>
                <button onClick={onCancelPreview} disabled={isUploading} style={{
                  background: "#fff", color: "#6b7280", border: "2px solid #e5e7eb", padding: "16px 24px",
                  borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: isUploading ? "not-allowed" : "pointer" }}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { filePickerOpenRef.current = true; fileInputRef.current?.click(); }}
                  disabled={isUploading} style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", border: "none",
                    padding: "16px 24px", borderRadius: 12, fontSize: 16, fontWeight: 600,
                    cursor: isUploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 10, opacity: isUploading ? 0.7 : 1,
                    boxShadow: "0 4px 12px rgba(102,126,234,0.4)" }}>
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#fff" strokeWidth={2}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Upload from Device
                </button>
                {profilePicture && profilePicture !== DEFAULT_AVATAR && (
                  <button onClick={onRemove} disabled={isUploading} style={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "#fff", border: "none",
                    padding: "16px 24px", borderRadius: 12, fontSize: 16, fontWeight: 600,
                    cursor: isUploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 10, opacity: isUploading ? 0.7 : 1,
                    boxShadow: "0 4px 12px rgba(239,68,68,0.4)" }}>
                    {isUploading ? "Removing…" : (<><svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#fff" strokeWidth={2}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Remove DP</>)}
                  </button>
                )}
              </>
            )}
          </div>

          <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChange} style={{ display: "none" }} />

          <div style={{ marginTop: 20, padding: 12, background: "#f3f4f6", borderRadius: 8,
            fontSize: 13, color: "#6b7280", textAlign: "center" }}>
            <p style={{ margin: 0 }}>📸 Supported formats: JPG, PNG, GIF<br />Maximum file size: 5MB</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// App shell — pixel-matches Worker.jsx's sidebar + topbar
function Shell({ view, onNavigate, children, search, onSearchChange }) {
  const auth = useAuth() || {};
  const { user, handleLogout } = auth;
  const initials = getInitials(user?.name);

  // ── responsive ──
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const doLogout = () => {
    if (typeof handleLogout === "function") handleLogout();
    else window.location.href = `${BASE}/auth/logout`;
  };
  const stroke = (on) => (on ? "#fff" : "rgba(255,255,255,0.7)");

  // ── Profile picture state (mirrors Worker.jsx) ──
  const [profilePicture, setProfilePicture] = useState(DEFAULT_AVATAR);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const fileInputRef = useRef(null);
  const filePickerOpenRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    api("/api/user/profile-picture").then(d => setProfilePicture(d?.profile_picture || DEFAULT_AVATAR)).catch(() => {});
  }, [user]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); e.target.value = null; return; }
    if (file.size > 5 * 1024 * 1024) { alert("File size must be less than 5MB"); e.target.value = null; return; }
    const reader = new FileReader();
    reader.onloadend = () => { filePickerOpenRef.current = false; setPreviewImage(reader.result); };
    reader.readAsDataURL(file);
    e.target.value = null;
  };
  const uploadProfilePicture = async () => {
    if (!previewImage) return;
    setIsUploadingProfile(true);
    try {
      const d = await api("/api/user/profile-picture", { method: "POST", body: JSON.stringify({ profile_picture: previewImage }) });
      setProfilePicture(d?.profile_picture || previewImage);
      setShowProfileModal(false); setPreviewImage(null);
    } catch (e) { alert(`Failed to upload profile picture: ${e.message}`); }
    finally { setIsUploadingProfile(false); }
  };
  const removeProfilePicture = async () => {
    setIsUploadingProfile(true);
    try {
      const d = await api("/api/user/profile-picture", { method: "DELETE" });
      setProfilePicture(d?.profile_picture || DEFAULT_AVATAR); setPreviewImage(null);
    } catch (e) { alert(`Failed to remove profile picture: ${e.message}`); }
    finally { setIsUploadingProfile(false); }
  };
  const closeProfileModal = () => {
    if (filePickerOpenRef.current) return;
    setShowProfileModal(false); setPreviewImage(null);
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        input[type="date"], input[type="time"] { -webkit-appearance: none; }
      `}</style>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "100vh", fontFamily: FONT, background: "#f0ede8" }}>
        {/* Sidebar / Mobile top nav */}
        <div style={{ width: isMobile ? "100%" : 72, background: "#101F40", display: "flex",
          flexDirection: isMobile ? "row" : "column",
          alignItems: "center", padding: isMobile ? "8px 16px" : "20px 0", gap: isMobile ? 4 : 8,
          flexShrink: 0, position: "sticky", top: 0, height: isMobile ? "auto" : "100vh", zIndex: 20,
          overflowX: isMobile ? "auto" : "visible",
          justifyContent: isMobile ? "space-between" : "flex-start" }}>
          <div style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, backgroundImage: "url(/Images/Manurevalogo.png)",
            backgroundSize: "cover", backgroundPosition: "center", borderRadius: 12,
            marginBottom: isMobile ? 0 : 16, flexShrink: 0 }} />

          <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column",
            gap: isMobile ? 4 : 8, alignItems: "center",
            flex: isMobile ? 1 : "unset", justifyContent: isMobile ? "center" : "flex-start" }}>
            {/* user icon -> Overview */}
            <SideIcon active={view === "overview"} title="My Dashboard" isMobile={isMobile} onClick={() => onNavigate("overview")}>
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={stroke(view === "overview")}
                strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            </SideIcon>

            {/* calendar icon -> My Attendance */}
            <SideIcon active={view === "attendance"} title="My Attendance" isMobile={isMobile} onClick={() => onNavigate("attendance")}>
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={stroke(view === "attendance")}
                strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </SideIcon>

            {/* people icon -> Housekeeping */}
            <SideIcon active={view === "housekeeping"} title="Housekeeping" isMobile={isMobile} onClick={() => onNavigate("housekeeping")}>
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke={stroke(view === "housekeeping")}
                strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </SideIcon>
          </div>

          {!isMobile && <div style={{ flex: 1 }} />}
        </div>

        {/* Main column */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Topbar */}
          <div style={{ background: "#fff", padding: isMobile ? "0 12px" : "0 28px", height: isMobile ? 52 : 60,
            display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e8e4df",
            position: "sticky", top: isMobile ? 52 : 0, zIndex: 10, flexShrink: 0 }}>
            <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 600, color: "#101F40", letterSpacing: "-0.3px",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? 150 : "unset" }}>
              Manureva Digital Solutions
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 14 }}>
              {/* Mobile search toggle */}
              {isMobile && (
                <button title="Search" onClick={() => setShowMobileSearch(v => !v)}
                  style={{ width: 32, height: 32, borderRadius: "50%", background: "#f5f3ef", border: "none",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#555" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </button>
              )}
              {/* Desktop search — filters "Recent Leave Requests" on My Dashboard */}
              {!isMobile && (
                <div style={{ position: "relative" }}>
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#999" strokeWidth={2}
                    style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input type="text" placeholder="Search leave requests…" value={search || ""}
                    onChange={e => onSearchChange && onSearchChange(e.target.value)}
                    style={{ background: "#f5f3ef", border: "none", borderRadius: 20,
                      padding: "8px 16px 8px 36px", fontSize: 13, color: "#555", width: isTablet ? 160 : 220,
                      outline: "none", fontFamily: FONT }} />
                </div>
              )}

               {/* <WorkerTickets user={user} isMobile={isMobile} /> */}
              <button title="Notifications" style={{ width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: "50%",
                background: "#f5f3ef", border: "none", cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} fill="none" stroke="#555"
                  strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </button>
              <div title="Edit profile picture" onClick={() => setShowProfileModal(true)}
                style={{ width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: "50%",
                background: "#4caf4f", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: isMobile ? 11 : 13, fontWeight: 600, color: "#fff", cursor: "pointer", flexShrink: 0, overflow: "hidden" }}>
                {profilePicture && profilePicture !== DEFAULT_AVATAR
                  ? <img src={profilePicture} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={e => { e.target.style.display = "none"; }} />
                  : initials}
              </div>
              <button onClick={doLogout} title="Logout" style={{ width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: "50%",
                border: "1px solid #e8e4df", background: "transparent", cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} fill="none" stroke="#888"
                  strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile expandable search */}
          {isMobile && showMobileSearch && (
            <div style={{ padding: "8px 12px", background: "#fff", borderBottom: "1px solid #e8e4df",
              position: "sticky", top: 104, zIndex: 9 }}>
              <div style={{ position: "relative" }}>
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#999" strokeWidth={2}
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input autoFocus type="text" placeholder="Search leave requests…" value={search || ""}
                  onChange={e => onSearchChange && onSearchChange(e.target.value)}
                  style={{ width: "100%", background: "#f5f3ef", border: "none", borderRadius: 20,
                    padding: "8px 16px 8px 32px", fontSize: 13, color: "#555", outline: "none",
                    fontFamily: FONT, boxSizing: "border-box" }} />
              </div>
            </div>
          )}

          {/* Content */}
          <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>
        </div>
      </div>

      {showProfileModal && (
        <ProfilePictureModal
          profilePicture={profilePicture}
          previewImage={previewImage}
          isUploading={isUploadingProfile}
          filePickerOpenRef={filePickerOpenRef}
          fileInputRef={fileInputRef}
          onFileChange={onFileChange}
          onUpload={uploadProfilePicture}
          onRemove={removeProfilePicture}
          onCancelPreview={() => setPreviewImage(null)}
          onClose={closeProfileModal}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function NetworkManager() {
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const [mode, setMode] = useState("me");
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const onToast = useCallback((kind, msg) => {
    setToast({ kind: kind === "ok" ? "ok" : "err", msg });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const [meTab, setMeTab] = useState("overview"); // 'overview' | 'attendance'
  const [topSearch, setTopSearch] = useState(""); // topbar search — filters Recent Leave Requests

  const [staff, setStaff] = useState([]);
  const [hkLoading, setHkLoading] = useState(false);
  const [hkErr, setHkErr] = useState(null);
  const [selId, setSelId] = useState(null);
  const [hkTab, setHkTab] = useState("leave");
  const [query, setQuery] = useState("");
  const [formKey, setFormKey] = useState(0); // bump to reset the on-behalf form after submit/cancel

  // Unified view for the sidebar icons: 'overview' | 'attendance' | 'housekeeping'
  const view = mode === "housekeeping" ? "housekeeping" : meTab;
  const navigate = (v) => {
    if (v === "housekeeping") { setMode("housekeeping"); }
    else { setMode("me"); setMeTab(v); }   // 'overview' | 'attendance'
  };

  useEffect(() => {
    if (mode !== "housekeeping" || staff.length) return;
    setHkLoading(true); setHkErr(null);
    api("/api/network-manager/housekeeping")
      .then(list => { setStaff(list || []); if (list?.length) setSelId(list[0].id); })
      .catch(e => setHkErr(e.message))
      .finally(() => setHkLoading(false));
  }, [mode]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? staff.filter(s => (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q)) : staff;
  }, [staff, query]);
  const selected = staff.find(s => s.id === selId) || null;

  const subTab = (active) => ({ padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer",
    background: "none", border: "none", color: active ? C.accent : C.muted,
    borderBottom: `2px solid ${active ? C.accent : "transparent"}`, marginBottom: -1 });

  return (
    <Shell view={view} onNavigate={navigate} search={topSearch} onSearchChange={setTopSearch}>
      <div style={{ padding: isMobile ? 12 : isTablet ? 18 : 28, fontFamily: FONT }}>
      <Toast toast={toast} />

      {mode === "me" ? (
        <div>
          {meTab === "overview"
            ? <MyOverview onToast={onToast} search={topSearch} />
            : <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${C.line}`, padding: isMobile ? 14 : 22 }}>
                <AttendancePanel target={null} onToast={onToast} />
              </div>}
        </div>
      ) : (
        hkErr ? <div style={{ color: C.red, background: "#fff", padding: 20, borderRadius: 16, border: `1px solid ${C.line}` }}>{hkErr}</div>
        : hkLoading ? <div style={{ color: C.muted }}>Loading housekeeping staff…</div>
        : staff.length === 0 ? (
          <div style={{ color: C.muted, background: "#fff", padding: 24, borderRadius: 16, border: `1px solid ${C.line}` }}>
            No housekeeping employees found. Create employees with the role “Housekeeping” first.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px 1fr", gap: isMobile ? 12 : 20, alignItems: "start" }}>
            <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.line}`, overflow: "hidden" }}>
              <div style={{ padding: 12, borderBottom: `1px solid ${C.line}` }}>
                <input style={{ ...input, borderRadius: 999, background: "#f5f3ef", border: "none" }} placeholder="Search…" value={query}
                  onChange={e => setQuery(e.target.value)} /></div>
              <div style={{ maxHeight: isMobile ? 320 : 560, overflowY: "auto" }}>
                {filtered.map(s => {
                  const active = s.id === selId;
                  return (
                    <div key={s.id} onClick={() => setSelId(s.id)}
                      style={{ padding: "12px 14px", cursor: "pointer", borderBottom: `1px solid #f0ede8`,
                        background: active ? C.accentBg : "#fff",
                        borderLeft: `3px solid ${active ? C.accent : "transparent"}` }}>
                      <div style={{ fontWeight: 600, color: C.ink, fontSize: 14 }}>{s.name}</div>
                      <div style={{ color: C.faint, fontSize: 12 }}>{s.email}</div>
                      {!s.has_attendance && <div style={{ color: C.amber, fontSize: 11, marginTop: 3 }}>no attendance record</div>}
                    </div>
                  );
                })}
                {filtered.length === 0 && <div style={{ padding: 16, color: C.muted, fontSize: 13 }}>No match.</div>}
              </div>
            </div>
            <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.line}`, padding: isMobile ? 14 : 22 }}>
              {selected && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: C.ink }}>{selected.name}</div>
                    <div style={{ color: C.faint, fontSize: 13 }}>
                      {selected.email}{selected.employee_code ? `  ·  ${selected.employee_code}` : ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, borderBottom: `1px solid ${C.line}`, marginBottom: 20 }}>
                    <button style={subTab(hkTab === "leave")} onClick={() => setHkTab("leave")}>Apply Leave</button>
                    <button style={subTab(hkTab === "attendance")} onClick={() => setHkTab("attendance")}>Attendance</button>
                  </div>
                  {hkTab === "leave"
                    ? <LeaveForm key={`${selected.id}-${formKey}`} variant="inline" target={selected} onToast={onToast}
                        onDone={() => setFormKey(k => k + 1)} onCancel={() => setFormKey(k => k + 1)} />
                    : <AttendancePanel key={selected.id} target={selected} onToast={onToast} />}
                </>
              )}
            </div>
          </div>
        )
      )}
      </div>
    </Shell>
  );
}
